import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, map, of, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { NavigationMenuSelection } from "../interfaces/navigation_menu.interface";

import { AssessmentFooterPage, AssessmentFooterPageQuery, readAssessmentFooterPage } from "./assessment_footer_page";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface QuestionResponseCommentDto {
    id: number;
    userId?: number | null;
    user_id?: number | null;
    body: string;
    userRole?: string | null;
    user_role?: string | null;
    createdAt?: string | null;
    created_at?: string | null;
    updatedAt?: string | null;
    updated_at?: string | null;
    questionReference?: string | null;
    question_reference?: string | null;
    createdByDisplayName?: string | null;
    created_by_display_name?: string | null;
}

export interface CreateQuestionResponseCommentPayload {
    body: string;
}

@Injectable({ providedIn: "root" })
export class QuestionResponseCommentsService {
    private static readonly SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
    private static readonly TARGET_PATTERN = /^[A-Za-z0-9 _.-]+$/;

    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");
    private activeProductLicenseId: number | null = null;

    constructor(private readonly http: HttpClient) {}

    setActiveProductLicenseId(productLicenseId: number | null): void {
        if (productLicenseId === null || productLicenseId === undefined) {
            this.activeProductLicenseId = null;
            return;
        }

        const normalized = Number(productLicenseId);
        this.activeProductLicenseId = Number.isInteger(normalized) && normalized > 0 ? normalized : null;
    }

    listComments(
        selection?: NavigationMenuSelection | null,
        productLicenseId?: number
    ): Observable<QuestionResponseCommentDto[]> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before loading assessment notes."));
        }

        const normalized = this.normalizeSelection(selection ?? null);
        const query: Record<string, string> = { productLicenseId: String(licenseId) };
        if (normalized) {
            query["section"] = normalized.section;
            query["target"] = normalized.target;
        }

        const params = new HttpParams({ fromObject: query });

        return this.http
            .get<ApiResponse<QuestionResponseCommentDto[]>>(`${this.apiBaseUrl}/question-response-comments`, { params })
            .pipe(
                map((response) => (response?.success && Array.isArray(response.data) ? response.data : [])),
                catchError((error) => {
                    if (error?.status === 404) {
                        return of([]);
                    }
                    return throwError(() => error);
                })
            );
    }

    listPage(selection: NavigationMenuSelection | null, productLicenseId: number,
        query: AssessmentFooterPageQuery): Observable<AssessmentFooterPage<QuestionResponseCommentDto>> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) return throwError(() => new Error("An assessment must be selected."));
        const normalized = this.normalizeSelection(selection);
        const params = new HttpParams({ fromObject: {
            productLicenseId: String(licenseId), page: String(query.page), pageSize: String(query.pageSize),
            search: query.search, authors: JSON.stringify(query.authors),
            ...(normalized ? { section: normalized.section, target: normalized.target } : {})
        } });
        return this.http.get<ApiResponse<AssessmentFooterPage<QuestionResponseCommentDto>>>(
            `${this.apiBaseUrl}/question-response-comments`, { params }).pipe(map(response => {
                if (!response?.success) throw new Error(response?.message || "Unable to load assessment footer.");
                return readAssessmentFooterPage(response.data, query);
            }));
    }

    createComment(
        selection: NavigationMenuSelection | null | undefined,
        payload: CreateQuestionResponseCommentPayload,
        productLicenseId?: number
    ): Observable<QuestionResponseCommentDto> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before adding assessment notes."));
        }

        const normalized = this.normalizeSelection(selection ?? null);

        const note = typeof payload.body === "string" ? payload.body : "";
        const body: Record<string, unknown> = {
            productLicenseId: licenseId,
            note
        };

        if (normalized) {
            body["section"] = normalized.section;
            body["target"] = normalized.target;
        }

        return this.http
            .post<ApiResponse<QuestionResponseCommentDto>>(`${this.apiBaseUrl}/question-response-comments`, body)
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to add assessor note.");
                    }
                    return response.data;
                })
            );
    }

    updateComment(
        commentId: number,
        selection: NavigationMenuSelection | null | undefined,
        payload: CreateQuestionResponseCommentPayload,
        productLicenseId?: number
    ): Observable<QuestionResponseCommentDto> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before editing assessment notes."));
        }
        if (!Number.isInteger(commentId) || commentId <= 0) {
            return throwError(() => new Error("A valid assessor note identifier is required."));
        }

        const normalized = this.normalizeSelection(selection ?? null);
        const body: Record<string, unknown> = {
            productLicenseId: licenseId,
            note: typeof payload.body === "string" ? payload.body : ""
        };
        if (normalized) {
            body["section"] = normalized.section;
            body["target"] = normalized.target;
        }

        return this.http
            .patch<ApiResponse<QuestionResponseCommentDto>>(`${this.apiBaseUrl}/question-response-comments/${commentId}`, body)
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data || Number(response.data.id) !== commentId) {
                        throw new Error(response?.message || "Failed to edit assessor note.");
                    }
                    return response.data;
                })
            );
    }

    deleteComment(
        commentId: number,
        selection?: NavigationMenuSelection | null,
        productLicenseId?: number
    ): Observable<void> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before removing assessment notes."));
        }

        const normalized = this.normalizeSelection(selection ?? null);

        if (!Number.isInteger(commentId) || commentId <= 0) {
            return throwError(() => new Error("A valid assessor note identifier is required."));
        }

        const query: Record<string, string> = { productLicenseId: String(licenseId) };
        if (normalized) {
            query["section"] = normalized.section;
            query["target"] = normalized.target;
        }

        const params = new HttpParams({ fromObject: query });

        return this.http
            .delete<ApiResponse<{ id: number }>>(`${this.apiBaseUrl}/question-response-comments/${commentId}`, { params })
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Failed to delete assessor note.");
                    }
                })
            );
    }

    private resolveProductLicenseId(productLicenseId?: number): number | null {
        const candidate = productLicenseId ?? this.activeProductLicenseId;
        const normalized = Number(candidate);

        if (!Number.isInteger(normalized) || normalized <= 0) {
            return null;
        }

        return normalized;
    }

    private normalizeSelection(selection: NavigationMenuSelection | null | undefined): NavigationMenuSelection | null {
        if (!selection) {
            return null;
        }

        const section = typeof selection.section === "string" ? selection.section.trim() : "";
        const target = typeof selection.target === "string" ? selection.target.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!QuestionResponseCommentsService.SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!QuestionResponseCommentsService.TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }
}
